import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { PageContainer, PageHeader, PageTitle, Button, Loader, Modal, SubSectionTitle, Textarea, Divider } from '../styles/PageStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faTrash } from '@fortawesome/free-solid-svg-icons';
import { getNodesForPipeline, getPipelineRuns, resumePipeline } from '../services/pipelineService';
import { useParams } from 'react-router-dom';
import { FaSpinner, FaTimes } from 'react-icons/fa';
import PipelineExecutionDrawer from '../components/pipeline/PipelineExecutionDrawer';
import Pagination from '../components/common/Pagination';
import { fetchCountByFilter } from '../services/entityService';
import { uploadFile } from '../services/fileUploadService';
import { useToast } from '../components/common/ToastProvider';

// Simple timeline item
const TimelineContainer = styled.div`
  max-width: 100%;
  padding: 1rem;
`;

const TimelineContent = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 1rem 1.5rem;
  width: calc(100% - 40px);
`;

const TimelineDate = styled.div`
  font-size: 0.95rem;
  color: #888;
  margin-bottom: 0.5rem;
`;

const TimelineStatus = styled.div`
  font-size: 1rem;
  font-weight: bold;
  color: ${({ status }) =>
    status === 'success' ? '#4caf50' :
      status === 'failed' ? '#f44336' :
        '#2196f3'};
  margin-bottom: 0.5rem;
`;

const TimelineTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;
const FloatingDrawer = styled.div`
  position: fixed;
  top: 63px; /* adjust if you have a header */
  right: 0;
  height: calc(100vh - 63px);
  background: #fff;
  border-left: 1px solid #eee;
  box-shadow: -2px 0 8px rgba(0,0,0,0.07);
  min-width: 280px;
  max-width: 700px;
  width: ${props => props.width}px;
  transition: width 0.2s;
  z-index: 1002;
  display: ${props => (props.open ? 'block' : 'none')};
  overflow: auto;
`;

const DrawerToggleButton = styled.button`
  position: fixed;
  top: 160px;
  right: ${props => (props.drawerOpen ? `${props.drawerWidth + 10}px` : '10px')};
  z-index: 1003;
  background: #ffffff;
  color: #666;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  cursor: pointer;
`;

const DrawerResizer = styled.div`
  width: 6px;
  cursor: ew-resize;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #e0e0e0;
  z-index: 1004;
  &:hover {
    background: #bdbdbd;
  }
`;

const PipelineAnalytics = () => {
  const { pipelineId } = useParams();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pipelineInstanceId, setPipelineInstanceId] = useState(null);
  const [pipelineLoader, setPipelineLoader] = useState(false);
  const [analyticsDrawerOpen, setAnalyticsDrawerOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(740);
  const drawerResizing = useRef(false);
  const [executionResults, setExecutionResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pipelineName, setPipelineName] = useState('');
  const [pageNumberNode, setPageNumberNode] = useState(0);
  const [pageSizeNode] = useState(10);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [currentPipelineStatus, setCurrentPipelineStatus] = useState(null);
  const [actualPipelineStatus, setActualPipelineStatus] = useState(null); // Store the server pipeline status
  const [totalExecutionResults, setTotalExecutionResults] = useState(0);
  const [pipelineCode , setPipelineCode] = useState(null);


  // Add pagination state variables for pipeline runs
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Resume modal state variables
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeInput, setResumeInput] = useState('{}');
  const [resumeUploadedDocuments, setResumeUploadedDocuments] = useState([]);
  const [runError, setRunError] = useState(null);
  
  const { showToast } = useToast();
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(10);
  const [paginationLoading, setPaginationLoading] = useState(false);

  // Utility function to merge new results with existing ones, avoiding duplicates
  const mergeUniqueResults = useCallback((currentResults, newResults, replaceAll = false) => {
    if (!newResults || newResults.length === 0) {
      return replaceAll ? [] : currentResults;
    }

    if (replaceAll) {
      return newResults;
    }

    const existingIds = new Set(currentResults.map(result => result.id));
    const uniqueNewResults = newResults.filter(result => !existingIds.has(result.id));

    return uniqueNewResults.length > 0 ? [...currentResults, ...uniqueNewResults] : currentResults;
  }, []);

  // Function to check if pipeline status requires polling
  const shouldContinuePolling = useCallback((status) => {
    const finalStatuses = ['WAITING', 'ERRORED', 'CLOSED', 'COMPLETED'];
    return status === 'INPROGRESS' || !finalStatuses.includes(status);
  }, []);

  // Function to get current pipeline status from execution results
  const getCurrentPipelineStatus = useCallback((results) => {
    if (!results || results.length === 0) return null;

    // Check if any node is still in progress
    const hasInProgress = results.some(result => result.transaction_status === 'INPROGRESS');
    if (hasInProgress) return 'INPROGRESS';

    // Check if any node has errors
    const hasErrors = results.some(result => result.transaction_status === 'ERRORED');
    if (hasErrors) return 'ERRORED';

    // Check if all nodes are completed
    const allClosed = results.every(result => result.transaction_status === 'CLOSED');
    if (allClosed) return 'CLOSED';

    return 'WAITING';
  }, []);

  // Polling function to refresh pipeline status
  const startPolling = useCallback((targetPipelineId = null) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    const pipelineIdToUse = targetPipelineId || pipelineInstanceId;
    if (!pipelineIdToUse) return;

    const interval = setInterval(async () => {
      try {
        const filter = { pipeline_id: pipelineIdToUse };
        const sort = { created_at: "ASC" };
        const response = await getNodesForPipeline(filter, sort, `?skip=0&limit=${pageSizeNode}`);
        const uniqueResults = mergeUniqueResults([], response, true);

        setExecutionResults(uniqueResults);

        const newStatus = getCurrentPipelineStatus(uniqueResults);
        setCurrentPipelineStatus(newStatus);

        // Stop polling if pipeline has reached a final state
        if (!shouldContinuePolling(newStatus)) {
          clearInterval(interval);
          setPollingInterval(null);
          console.log(`Pipeline polling stopped. Final status: ${newStatus}`);
        }
      } catch (error) {
        console.error('Error during polling:', error);
        // Continue polling even on error, but log it
      }
    }, 10000); // Poll every 10 seconds to match PipelineDesigner

    setPollingInterval(interval);
  }, [pipelineInstanceId, pageSizeNode, mergeUniqueResults, getCurrentPipelineStatus, shouldContinuePolling, pollingInterval]);

  // Stop polling function
  const stopPolling = useCallback(() => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [pollingInterval]);

  // Resume pipeline execution function
  const resumePipelineExecution = useCallback(async () => {
    if (!pipelineInstanceId) return;

    setRunError(null);
    setIsRunning(true);

    try {
      let resumeData = {
        "async_execution": true,
        "pipeline_id":pipelineInstanceId
      };
      
      // Parse JSON input if provided
      if (resumeInput && resumeInput.trim() !== '{}') {
        try {
          resumeData['payload'] = JSON.parse(resumeInput);
        } catch (e) {
          setRunError('Invalid JSON format in resume input');
          setIsRunning(false);
          return;
        }
      }

      // Handle uploaded documents
      if (resumeUploadedDocuments && resumeUploadedDocuments.length > 0) {
        // Upload files first if they are raw File objects
        const uploadedFiles = [];
        for (const [index, doc] of resumeUploadedDocuments.entries()) {
        const timestamp = Date.now();
          if (doc instanceof File) {
            try {
              const fileName = doc.name || `document_${index}`;

                const cleanFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '');
              const fileId = `${timestamp}_${cleanFileName}`;
              const uploadedFile = await uploadFile(doc, 'resumepipeline', pipelineName, fileId);
              uploadedFiles.push(uploadedFile.data.url);
            } catch (error) {
              console.error('Error uploading file:', error);
                showToast({
        message: `Failed to upload file "${doc.name}": ${error.message}`,
        type: 'error',
      });
              // toast.error(`Failed to upload file "${doc.name}": ${error.message}`);
            }
          } else {
            // Already uploaded file
            uploadedFiles.push({
              filename: doc.filename,
              content: doc.content,
              type: doc.type
            });
          }
        }
        
        if (uploadedFiles.length > 0) {
          resumeData.document_urls = uploadedFiles;
        }
      }

      const response = await resumePipeline(pipelineCode, resumeData);
      console.log('Pipeline resumed:', response);
      showToast({
        message: 'Pipeline resumed successfully!',
        type:'success'
      })
      // toast.success('Pipeline resumed successfully!');
      
      // Close modal and reset state
      setShowResumeModal(false);
      setResumeInput('{}');
      setResumeUploadedDocuments([]);
      
      // Start polling to monitor the resumed pipeline
      startPolling(pipelineInstanceId);
      
    } catch (error) {
      console.error('Error resuming pipeline:', error);
      setRunError(error?.message || 'Failed to resume pipeline');
      showToast({
        message: 'Failed to resume pipeline:',
        type: error
      })
      // toast('Failed to resume pipeline: ' + (error?.message || 'Unknown error'));
    } finally {
      setIsRunning(false);
    }
  }, [pipelineInstanceId, resumeInput, resumeUploadedDocuments, startPolling]);

  // Handle file upload for resume
  const handleResumeFileUpload = useCallback(async (file) => {
    try {
      const uploadedFile = await uploadFile(file);
      const newDocument = {
        id: Date.now(),
        filename: file.name,
        content: uploadedFile.url,
        type: file.type
      };
      setResumeUploadedDocuments(prev => [...prev, newDocument]);
      showToast({
        message: `File "${file.name}" uploaded successfully!`,
        type: 'success'
      })
      // toast.success(`File "${file.name}" uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading file:', error);
       showToast({
        message: 'Failed to upload file: ' + (error.message || 'Unknown error'),
        type: 'error'
      })
      // toast.error('Failed to upload file: ' + (error.message || 'Unknown error'));
    }
  }, []);

  // Remove uploaded document from resume
  const removeResumeDocument = useCallback((documentId) => {
    setResumeUploadedDocuments(prev => prev.filter(doc => doc.id !== documentId));
  }, []);

  // useEffect(() => {
  //   // Fetch pipeline runs for this pipeline
  //   const fetchRuns = async () => {
  //     setLoading(true);
  //     try {
  //       let filter = {
  //         code: pipelineId,
  //       }
  //       const data = await getPipelineRuns(filter);
  //       setRuns(data || []);
  //     } catch (err) {
  //       setRuns([]);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  //   setPipelineInstanceId(pipelineId);
  //   fetchRuns();
  // }, [pipelineId]);

  useEffect(() => {
    // Fetch pipeline runs for this pipeline with pagination
    const fetchRuns = async (page = 1) => {
      setLoading(true);
      try {
        let filter = {
          code: pipelineId,
        };

        // Add pagination parameters
        const skip = (page - 1) * pageSize;
        const queryParams = `?skip=${skip}&limit=${pageSize}`;

        const response = await getPipelineRuns(filter, {}, queryParams);

        // Handle response structure - adjust based on your API response
        if (response && typeof response === 'object') {
          // If response has pagination info
          if (response.data && Array.isArray(response.data)) {
            setRuns(response.data || []);
            setTotalItems(response.total || response.count || response.data.length);
            setTotalPages(Math.ceil((response.total || response.count || response.data.length) / pageSize));
          } else if (Array.isArray(response)) {
            // If response is just an array (no pagination info from server)
            setRuns(response);
            setTotalItems(response.length);
            setTotalPages(Math.ceil(response.length / pageSize));
          } else {
            setRuns([]);
            setTotalItems(0);
            setTotalPages(1);
          }
        } else if (Array.isArray(response)) {
          setRuns(response);
          setTotalItems(response.length);
          setTotalPages(Math.ceil(response.length / pageSize));
        } else {
          setRuns([]);
          setTotalItems(0);
          setTotalPages(1);
        }
      } catch (err) {
        console.error('Error fetching pipeline runs:', err);
        setRuns([]);
        setTotalItems(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    setPipelineInstanceId(pipelineId);
    setPipelineCode(pipelineCode)
    fetchRuns(currentPage);
  }, [pipelineId, currentPage, pageSize, pipelineCode]);
  // Function to handle page changes for pipeline runs
  const handleRunsPageChange = useCallback(async (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage) {
      return;
    }

    setPaginationLoading(true);

    try {
      let filter = {
        code: pipelineId,
      };

      const skip = (newPage - 1) * pageSize;
      const queryParams = `?skip=${skip}&limit=${pageSize}`;

      const response = await getPipelineRuns(filter, {}, queryParams);

      if (response && typeof response === 'object') {
        if (response.data && Array.isArray(response.data)) {
          setRuns(response.data || []);
          // Update total items if provided in response
          if (response.total !== undefined) {
            setTotalItems(response.total);
            setTotalPages(Math.ceil(response.total / pageSize));
          }
        } else if (Array.isArray(response)) {
          setRuns(response);
        } else {
          setRuns([]);
        }
      } else if (Array.isArray(response)) {
        setRuns(response);
      } else {
        setRuns([]);
      }

      setCurrentPage(newPage);
    } catch (err) {
      console.error('Error fetching pipeline runs for page:', newPage, err);
      setRuns([]);
    } finally {
      setPaginationLoading(false);
    }
  }, [pipelineId, pageSize, totalPages, currentPage]);

  // Update setCurrentPage function to use handleRunsPageChange
  const updateCurrentPage = useCallback((newPage) => {
    handleRunsPageChange(newPage);
  }, [handleRunsPageChange]);

  const updateTotalPages = useCallback((newTotalPages) => {
    setTotalPages(newTotalPages);
  }, []);



  const nodesDetails = async (e, run) => {
    // console.log('Selected run:', run);
    setPipelineInstanceId(run.pipeline_id);
    setPipelineCode(run.code);
    setAnalyticsDrawerOpen(false);
    setCurrentPipelineStatus(null);
    // Stop any existing polling
    stopPolling();

    // Reset pagination
    setPageNumberNode(0);
    setIsLoadingMore(false);
    setHasMorePages(true);

    let filter = { pipeline_id: run.pipeline_id };
    let sort = { created_at: "ASC" };

    //FirsT PaGE Fetch
    const response = await getNodesForPipeline(filter, sort, `?skip=0&limit=${pageSizeNode}`);
    const uniqueResults = mergeUniqueResults([], response, true); // Replace all with new results
    const getTotalCount = await fetchCountByFilter('node_execution', { pipeline_id: run.pipeline_id });
    setTotalExecutionResults(getTotalCount);

    setExecutionResults(uniqueResults);
    setPipelineName(run.name);

    // Check if more pages based on total count
    const hasMore = uniqueResults.length < getTotalCount;
    setHasMorePages(hasMore);

    // console.log(`Initial load: ${uniqueResults.length} items loaded, ${getTotalCount} total available, hasMore: ${hasMore}`);

    const actualPipelineStatus = run.transaction_status || 'Unknown';
    // console.log(`PipelineAnalytics - Setting status from run: ${actualPipelineStatus}`);
    setCurrentPipelineStatus(actualPipelineStatus);
    setActualPipelineStatus(actualPipelineStatus); // Store the server status

    if (shouldContinuePolling(actualPipelineStatus)) {
      console.log(`Pipeline is in ${actualPipelineStatus} status, starting polling...`);
      // Start polling after a short delay to ensure state is updated
      setTimeout(() => {
        startPolling(run.pipeline_id);
      }, 1000);
    }

    setAnalyticsDrawerOpen(true);
    e.stopPropagation();
  }

  const refreshResults = useCallback(async () => {
    setPipelineLoader(true);
    if (!pipelineInstanceId) return;
    try {
      // Reset pagination and fetch first page
      setPageNumberNode(0);
      setIsLoadingMore(false);
      setHasMorePages(true);

      const filter = { pipeline_id: pipelineInstanceId };
      const sort = { created_at: "ASC" };
      const response = await getNodesForPipeline(filter, sort, `?skip=0&limit=${pageSizeNode}`);
      const uniqueResults = mergeUniqueResults([], response, true); // Replace all with new results
      // Get total count to properly set pagination
      console.log("Pipeline Instance Id", pipelineInstanceId)

      const getTotalCount = await fetchCountByFilter('node_execution', { pipeline_id: pipelineInstanceId });
      setTotalExecutionResults(getTotalCount.count || 0);

      setExecutionResults(uniqueResults);

      // Check if there are more pages based on total count
      const hasMore = uniqueResults.length < getTotalCount.count;
      setHasMorePages(hasMore);

      console.log(`Refresh: ${uniqueResults.length} items loaded, ${getTotalCount.count} total available, hasMore: ${hasMore}`);

      // Update pipeline status and manage polling
      // Use the stored actual pipeline status instead of deriving from nodes
      // Only update if we have a stored status, otherwise derive from nodes as fallback
      const statusToUse = actualPipelineStatus || getCurrentPipelineStatus(uniqueResults);
      setCurrentPipelineStatus(statusToUse);

      // Start or stop polling based on status
      if (shouldContinuePolling(statusToUse)) {
        if (!pollingInterval) {
          console.log(`Pipeline status: ${statusToUse}, starting polling...`);
          setTimeout(() => {
            startPolling(pipelineInstanceId);
          }, 1000);
        }
      } else {
        console.log(`Pipeline status: ${statusToUse}, stopping polling...`);
        stopPolling();
      }

      setPipelineLoader(false);
    }
    catch (error) {
      setPipelineLoader(false);
      console.error('Error refreshing results:', error);
    }
  }, [pipelineInstanceId, pageSizeNode, mergeUniqueResults, getCurrentPipelineStatus, shouldContinuePolling, pollingInterval, startPolling, stopPolling, actualPipelineStatus]);

  // Handle pagination for infinite scroll
  const handlePaginationChange = useCallback(async (event) => {
    if (event === 'next' && !isLoadingMore && hasMorePages) {
      setIsLoadingMore(true);
      const nextPage = pageNumberNode + 1;

      try {
        // First, check total count to see if there are more items
        const totalCount = await fetchCountByFilter('node_execution', { pipeline_id: pipelineInstanceId });
        const currentItemsCount = executionResults.length;

        // console.log(`Total items in DB: ${totalCount}, Currently loaded: ${currentItemsCount}`);

        // If we already have all items, don't fetch more
        if (currentItemsCount >= totalCount.count) {
          console.log('All items already loaded, no more pagination needed');
          setHasMorePages(false);
          setIsLoadingMore(false);
          return;
        }

        const filter = { pipeline_id: pipelineInstanceId };
        const sort = { created_at: "ASC" };

        // Fetch next page with skip and limit
        const response = await getNodesForPipeline(
          filter,
          sort,
          `?skip=${nextPage * pageSizeNode}&limit=${pageSizeNode}`
        );

        if (response && response.length > 0) {
          // Use utility function to merge unique results
          setExecutionResults(prevResults => {
            const newResults = mergeUniqueResults(prevResults, response);
            // console.log(`Added ${response.length} new items, total now: ${newResults.length}`);
            return newResults;
          });
          setPageNumberNode(nextPage);

          // Check if we've reached the end based on total count
          const newTotalLoaded = currentItemsCount + response.length;
          if (newTotalLoaded >= totalCount.count || response.length < pageSizeNode) {
            // console.log('Reached end of data - no more pages available');
            setHasMorePages(false);
          }
        } else {
          // No data returned, reached the end
          console.log('No more data returned from API');
          setHasMorePages(false);
        }
      } catch (error) {
        console.error('Error loading more results:', error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [pipelineInstanceId, pageNumberNode, pageSizeNode, isLoadingMore, hasMorePages, mergeUniqueResults, executionResults.length]);
  const onDrawerMouseDown = () => {
    drawerResizing.current = true;
    document.body.style.cursor = 'ew-resize';
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (drawerResizing.current) {
        setDrawerWidth((prev) => Math.max(280, Math.min(700, prev - e.movementX)));
      }
    };
    const onMouseUp = () => {
      drawerResizing.current = false;
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // Cleanup polling on unmount or when drawer closes
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // Stop polling when drawer closes
  useEffect(() => {
    if (!analyticsDrawerOpen) {
      stopPolling();
    }
  }, [analyticsDrawerOpen, stopPolling]);

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>
          <FontAwesomeIcon icon={faChartLine} style={{ color: '#0066FF' }} /> Pipeline Analytics
        </PageTitle>
      </PageHeader>
      <TimelineContainer>
        {loading && <div>Loading timeline...</div>}
        {!loading && runs.length === 0 && <div>No runs found for this pipeline.</div>}
        {!loading &&
          runs.map((run) => (
            <TimelineContent key={run.id} onClick={(e) => { nodesDetails(e, run); }} style={{ marginBottom: '1rem', width: '100%' }}>
              <div className='d-flex justify-content-between align-items-center'>
                <TimelineTitle>
                  {run.name} # {run.id}
                </TimelineTitle>
                <TimelineDate>{new Date(run.updated_at).toLocaleString()}</TimelineDate>
              </div>
              <TimelineStatus status={run.transaction_status}>
                {run.transaction_status?.charAt(0).toUpperCase() + run.transaction_status?.slice(1)}
              </TimelineStatus>
              <div>
                {run.summary || 'No summary available.'}
              </div>
              {run.transaction_status === 'ERRORED' && (
                <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPipelineInstanceId(run.pipeline_id);
                      setPipelineCode(run.code);
                      setShowResumeModal(true);
                    }}
                    style={{ 
                      padding: '4px 8px', 
                      fontSize: '12px',
                      backgroundColor: '#28a745',
                      borderColor: '#28a745'
                    }}
                  >
                    Resume Pipeline
                  </Button>
                </div>
              )}
            </TimelineContent>
          ))}
      </TimelineContainer>
      {!loading && !paginationLoading && runs.length > 0 && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem 0', marginBottom: '2rem' }}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalItems}
            setCurrentPage={updateCurrentPage}
            setTotalPages={updateTotalPages}
          />
        </div>
      )}
      {/* Analytics Panel */}
      {analyticsDrawerOpen ? (
        <DrawerToggleButton
          onClick={() => setAnalyticsDrawerOpen((open) => !open)}
          drawerOpen={analyticsDrawerOpen}
          drawerWidth={drawerWidth}
        >
          <FaTimes size={22} />
        </DrawerToggleButton>
      ) : (<></>)}
      <FloatingDrawer open={analyticsDrawerOpen} width={drawerWidth} style={{ zIndex: 9991 }}>
        <DrawerResizer onMouseDown={onDrawerMouseDown} />
        {pipelineLoader ? (<Loader><FaSpinner size={22} className='spinner' /> </Loader>) : (<></>)}
        <PipelineExecutionDrawer
          isOpen={analyticsDrawerOpen}
          onClose={() => setAnalyticsDrawerOpen(false)}
          onRefresh={() => {
            refreshResults();
          }}
          onPaginationChange={(event) => {
            // console.log('Pagination event:', event);
            handlePaginationChange(event);
          }}
          onResume={() => setShowResumeModal(true)}
          executionResults={executionResults}
          isRunning={currentPipelineStatus === 'INPROGRESS' || !!pollingInterval}
          isLoadingMore={isLoadingMore}
          pipelineName={pipelineName}
          totalExecutionResults={totalExecutionResults}
          pipelineStatus={currentPipelineStatus}
          pollingInterval={pollingInterval}
        />
      </FloatingDrawer>

      {/* Resume Pipeline Modal */}
      {showResumeModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowResumeModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '2rem',
              width: '80%'
            }}
          >
            <SubSectionTitle>Resume Pipeline: {pipelineName}</SubSectionTitle>
            {currentPipelineStatus && (
              <div style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                background: currentPipelineStatus === 'COMPLETED' ? '#d4edda' :
                  ['ERROR', 'ERRORED'].includes(currentPipelineStatus?.toUpperCase()) ? '#f8d7da' :
                    '#f0f8ff',
                borderRadius: '4px',
                fontSize: '0.9rem',
                border: `1px solid ${currentPipelineStatus === 'COMPLETED' ? '#c3e6cb' :
                  ['ERROR', 'ERRORED'].includes(currentPipelineStatus?.toUpperCase()) ? '#f5c6cb' :
                    '#bee5eb'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>Status:</strong> {currentPipelineStatus || 'Unknown'}
                  </div>
                  {pollingInterval && (
                    <div style={{
                      fontSize: '0.8rem',
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#4caf50'
                      }}></div>
                      Checking every 10s...
                    </div>
                  )}
                </div>
                {actualPipelineStatus?.created_at && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                    Last updated: {new Date(actualPipelineStatus.created_at).toLocaleString()}
                  </div>
                )}
              </div>
            )}
            <Divider />
            {runError && (
              <div style={{
                padding: '0.75rem',
                background: '#ffe0e0',
                color: '#b00020',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                Error: {runError}
              </div>
            )}
            <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'top' }}>
              <div style={{ flex: '1 1 60%' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Pipeline Input (JSON)
                </label>
                <Textarea
                  value={resumeInput}
                  onChange={(e) => setResumeInput(e.target.value)}
                  style={{
                    width: '100%',
                    height: '200px',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem'
                  }}
                  placeholder='Enter JSON input for the pipeline'
                />
              </div>

              <div style={{ flex: '1 1 auto' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Upload Document (Optional)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <input
                    id="file-upload-resume"
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => {
                      const files = e.target.files;
                      console.log("initial files:", files);
                      if (files && files.length > 0) {
                        const newFiles = Array.from(files).filter(f => f.size > 0);

                        setResumeUploadedDocuments(prevState => {
                          const existingFiles = prevState || [];
                          const existingNamesAndSizes = new Set(
                            existingFiles.map(f => `${f.name}${f.size}`)
                          );
                          const uniqueNewFiles = newFiles.filter(
                            f => !existingNamesAndSizes.has(`${f.name}${f.size}`)
                          );
                          return [...existingFiles, ...uniqueNewFiles];
                        });
                      }
                      e.target.value = '';
                    }}

                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('file-upload-resume').click()}
                    style={{ minWidth: 120 }}
                  >
                    Upload File(s)
                  </Button>
                </div>
                {resumeUploadedDocuments && resumeUploadedDocuments.length > 0 && (
                  <div
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      marginTop: '0.75rem'
                    }}
                  >
                    {resumeUploadedDocuments.map((file, idx) => (
                      <div
                        key={file.name + file.size + idx}
                        style={{
                          background: '#e6f7ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: 4,
                          marginTop: '.5rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: 4,
                        }}
                      >
                        <span>
                          {file.name} <br /> <small>({Math.round(file.size / 1024)} KB)</small>
                        </span>
                        <Button
                          type="button"
                          style={{
                            background: '#ffeded',
                            color: '#b00020',
                            border: 'none',
                            borderRadius: 4,
                            padding: '2px 8px',
                            marginLeft: 8,
                            fontSize: 12,
                            cursor: 'pointer'
                          }}
                          onClick={() =>
                            setResumeUploadedDocuments(prev =>
                              prev.filter((_, i) => i !== idx)
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faTrash} size="sm" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <Button onClick={() => setShowResumeModal(false)}>Cancel</Button>
              {pollingInterval && (
                <Button
                  onClick={stopPolling}
                  style={{ background: '#ff6b6b', color: 'white' }}
                >
                  Stop Polling
                </Button>
              )}
              <Button
                primary
                onClick={resumePipelineExecution}
                disabled={isRunning}
              >
                {isRunning ? 'Resuming...' : 'Resume Pipeline'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </PageContainer>
  );
};

export default PipelineAnalytics;